/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type Ref } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Inline } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import { type RovoExperienceSidebarAPI } from '@atlassian/rovo-experience-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIButton, AIButtonRefreshed } from '../../../ai-button';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIProactiveNudge, AIProactiveNudgeRefreshed } from '../../../ai-proactive-nudge';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { getChatRefreshV2DynamicConfig } from '../../../common/utils/chat-refresh-v2-config';

const i18n = defineMessages({
	tooltipLabel: {
		id: 'ai-smart-button.tooltip.label',
		defaultMessage: 'Ask Rovo AI',
		description: 'Label for the AI smart button tooltip',
	},
});

const styles = cssMap({
	buttonWrapper: {
		position: 'relative',
	},
});

type AISmartButtonUIProps = {
	isDisabled: boolean;
	isRefreshed?: boolean;
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	product?: string;
	sidebarApi?: RovoExperienceSidebarAPI;
};

export const AISmartButtonUI = forwardRef<HTMLButtonElement, AISmartButtonUIProps>(
	(
		{
			onClick,
			isDisabled,
			onMouseEnter,
			onMouseLeave,
			sidebarApi,
			isRefreshed,
			product,
		}: AISmartButtonUIProps,
		ref: Ref<HTMLButtonElement>,
	) => {
		const { formatMessage } = useIntl();
		const { isNewIconEnabled } = getChatRefreshV2DynamicConfig();
		const showNewIcon = isNewIconEnabled ?? false;

		return (
			<Inline space="space.100" alignBlock="center" xcss={styles.buttonWrapper}>
				{isRefreshed && sidebarApi ? (
					<AIProactiveNudgeRefreshed sidebarApi={sidebarApi} product={product} />
				) : (
					<AIProactiveNudge />
				)}
				<Tooltip
					position="left"
					content={formatMessage(i18n.tooltipLabel)}
					hideTooltipOnClick
					ignoreTooltipPointerEvents
				>
					{isRefreshed && showNewIcon ? (
						<AIButtonRefreshed
							onClick={onClick}
							isDisabled={isDisabled}
							onMouseEnter={onMouseEnter}
							onMouseLeave={onMouseLeave}
							ref={ref}
						/>
					) : (
						<AIButton
							onClick={onClick}
							isDisabled={isDisabled}
							onMouseEnter={onMouseEnter}
							onMouseLeave={onMouseLeave}
							ref={ref}
						/>
					)}
				</Tooltip>
			</Inline>
		);
	},
);
